/**
 * AP-35 Detailblatt 8 — **Ist-Daten und Nachkalkulation** (Baustein `PROJECT_MGMT.CONTROLLING`),
 * dazu das Verbrauchsmaterial aus Blatt 7 4. Der Vertrag zwischen Backend, ERP und PWA.
 */

export const CONSUMABLE_SOURCES = ['MANUAL', 'SITE_REPORT', 'PWA', 'PWA_FREE'] as const
export type ConsumableSource = (typeof CONSUMABLE_SOURCES)[number]

export const CONSUMABLE_SOURCE_LABELS: Readonly<Record<ConsumableSource, string>> = {
  MANUAL: 'von Hand',
  SITE_REPORT: 'Regiebericht',
  PWA: 'Mitarbeiter-App',
  PWA_FREE: 'Mitarbeiter-App (frei)',
}

export interface ConsumableView {
  id: string
  /** AP-36 8.1: Stand der Zeile — die Maske schreibt dagegen. */
  updatedAt?: string
  positionId: string
  source: ConsumableSource
  articleId: string | null
  articleNumber: string | null
  name: string
  quantity: number
  unitId: string | null
  unitCode: string | null
  /** Nur mit `projects.price.view`. */
  unitPrice?: number | null
  purchasePrice?: number | null
  priceUnit: number
  discountPercent?: number | null
  deliveredAt: string
  userName: string | null
  note: string | null
  /** „Artikel zuordnen" — freier Text aus der App (Blatt 7 4.). */
  needsArticle: boolean
  salesLineId: string | null
}

/** Blatt 8 2.1 — eine Buchung in der Liste der Arbeitszeiten. */
export interface ActualTimeEntry {
  timeEntryId: string
  date: string
  from: string | null
  to: string | null
  hours: number
  userId: string
  userName: string | null
  qualificationName: string | null
  stepId: string | null
  stepNumber: number | null
  stepName: string | null
  phaseId: string | null
  phaseName: string | null
  isAssembly: boolean
  positionId: string | null
  positionNumber: number | null
  status: string
  isChecked: boolean
  costRate?: number | null
  costAmount?: number | null
  salesRate?: number | null
  billing: { salesLineId: string; deliveryNoteNumber: string | null; invoiceNumber: string | null } | null
  siteReportNumber: string | null
}

/** Eine Zelle der Matrix: Stunden (nur Arbeit) und Betrag (nur mit Preisrecht). */
export interface ControllingCell {
  hours?: number | null
  amount?: number | null
}

export interface ControllingRow {
  key: string
  label: string
  kind: 'PHASE' | 'UNASSIGNED' | 'SUM' | 'COST' | 'SALES' | 'MARGIN' | 'SUPPLEMENT' | 'FORECAST'
  calculation: ControllingCell | null
  planned: ControllingCell | null
  actual: ControllingCell | null
  /** verbucht − Projektdaten */
  difference: ControllingCell | null
  /** verbucht ÷ Projektdaten in Prozent */
  percent: number | null
  /** rot: Kosten über Plan, Verkauf unter Plan */
  alert: boolean
}

export interface ControllingColumnSource {
  available: boolean
  reason?: string
}

export interface ControllingMatrix {
  projectId: string
  positionId: string | null
  until: string
  progressPercent: number
  /** Kürzel der Einheit „Stunde" aus den Artikel-Einheiten; fehlt in älteren Ständen → „h". */
  hourUnit?: string
  rows: ControllingRow[]
  sources: {
    calculation: ControllingColumnSource
    hours: ControllingColumnSource
    procurement: ControllingColumnSource
    incomingInvoices: ControllingColumnSource
    sales: ControllingColumnSource
    prices: ControllingColumnSource
  }
  hints: { code: string; count?: number; amount?: number | null }[]
}

export const SNAPSHOT_TRIGGER_LABELS: Readonly<Record<'COMPLETED' | 'ARCHIVED' | 'MANUAL', string>> = {
  COMPLETED: 'bei Fertigstellung',
  ARCHIVED: 'bei Archivierung',
  MANUAL: 'festgehalten',
}
